import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, FileCheck, Bell, Plane, Users, DollarSign, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';

export const TravelSafe = () => {
  const navigate = useNavigate();

  const includedFeatures = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Annual Microchip Check & Verification",
      description: "ISO compliance scan, registration accuracy check, location confirmation, owner info updates"
    },
    {
      icon: <FileCheck className="w-6 h-6" />,
      title: "Comprehensive Compliance Review",
      description: "Review of rabies and core vaccination status/certificates, titre testing status/certificates and other destination-specific requirements."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "30-Minute Emergency Travel Consultation",
      description: "Tailored session for your destination with written summary and action plan"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "TravelSafe Readiness Report",
      description: "Comprehensive summary of your pet's travel preparation status and next steps"
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Year-Round Travel Alerts",
      description: "Expiring vaccination reminders, airline policy changes, seasonal travel advisories"
    }
  ];

  const whoItsFor = [
    "Hurricane season preparedness & emergency evacuation",
    "Planning future relocation",
    "Medical, family, or immigration emergencies",
    "Pet owners who prefer to plan ahead rather than react last-minute"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Image - Dog in Rain */}
      <div className="relative h-[400px] md:h-[500px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50 z-10"></div>
        <img 
          src="/wet-dog-looking-up.jpg"
          alt="Adorable wet dog looking up - showing the need for pet care and protection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              Every Pet Deserves Safe Travel
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto drop-shadow-md">
              Comprehensive pre-travel health assessments and compliance checks
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section - Teal + Navy Gradient */}
      <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzAtMS4xLS45LTItMi0ycy0yIC45LTIgMiAuOSAyIDIgMiAyLS45IDItMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="relative container mx-auto px-6 py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl mb-6 border border-white/20">
              <Plane className="w-10 h-10" />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              TravelSafe Pet Readiness Service
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-teal-50">
              Be Prepared. Stay Ready. Travel Safely.
            </p>
            
            <p className="text-lg md:text-xl mb-10 text-white/90 max-w-3xl mx-auto">
              Annual service keeping your pet travel-ready for hurricanes and other emergencies. 
              Always compliant, documented, and ready to go.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/book-travelsafe')}
                className="px-8 py-4 bg-white text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition-all duration-200 shadow-xl flex items-center justify-center gap-2"
              >
                Schedule Your TravelSafe Check
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="#pricing"
                className="px-8 py-4 bg-teal-800/50 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-teal-800/70 transition-all duration-200 border border-white/20"
              >
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* What is TravelSafe Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                🐾 What is TravelSafe?
              </h2>
            </div>
            
            <div className="prose prose-xl max-w-none">
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                TravelSafe is an annual pet travel readiness service designed to ensure your pet's documentation, identification, and compliance requirements are up to date and aligned with current international travel standards.
              </p>
              
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Whether you are planning ahead or preparing for unexpected situations such as relocation or emergency evacuation, TravelSafe helps you avoid last-minute stress and uncertainty.
              </p>
            </div>

            {/* Why TravelSafe Matters */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 mb-8 border border-red-100">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                🌍 Why TravelSafe Matters
              </h3>
              <p className="text-lg text-gray-700 mb-4 font-semibold">
                Pet travel regulations are strict — and they change.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Without proper preparation, pets may:
              </p>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1 text-xl">✗</span>
                  <span className="text-lg text-gray-700">Be delayed or denied entry</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1 text-xl">✗</span>
                  <span className="text-lg text-gray-700">Face extended quarantine periods</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1 text-xl">✗</span>
                  <span className="text-lg text-gray-700">Miss travel deadlines due to documentation gaps</span>
                </li>
              </ul>
              <p className="text-lg text-gray-800 font-semibold">
                TravelSafe ensures you are informed, prepared, and ready to act when needed.
              </p>
            </div>

            {/* What's Included */}
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 mb-8 border border-teal-100">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                ✔️ What's Included in Your Annual Service
              </h3>
              
              <div className="space-y-6">
                {/* Microchip Check */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 text-lg">Annual Microchip Check & Verification</h4>
                    <p className="text-gray-700 text-base">ISO compliance scan, registration accuracy check, location confirmation, owner info updates</p>
                  </div>
                </div>

                {/* Compliance Review */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 text-lg">Comprehensive Compliance Review</h4>
                    <p className="text-gray-700 text-base">Review of rabies and core vaccination status/certificates, titre testing status/certificates and other destination-specific requirements</p>
                  </div>
                </div>

                {/* Consultation */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 text-lg">30-Minute Emergency Travel Consultation</h4>
                    <p className="text-gray-700 text-base">Tailored session for your destination with written summary and action plan</p>
                  </div>
                </div>

                {/* Readiness Report */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 text-lg">TravelSafe Readiness Report</h4>
                    <p className="text-gray-700 text-base">Comprehensive summary of your pet's travel preparation status and next steps</p>
                  </div>
                </div>

                {/* Travel Alerts */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 text-lg">Year-Round Travel Alerts</h4>
                    <p className="text-gray-700 text-base">Expiring vaccination reminders, airline policy changes, seasonal travel advisories</p>
                  </div>
                </div>
              </div>
            </div>

            {/* TravelSafe Readiness Report */}
            <div className="bg-white rounded-2xl p-8 mb-8 border-2 border-gray-200 shadow-lg">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                📄 Your TravelSafe Readiness Report
              </h3>
              <p className="text-lg text-gray-700 mb-4">
                Following your assessment, you will receive a personalized TravelSafe Readiness Report outlining:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1 text-lg">•</span>
                  <span className="text-base text-gray-700">Your pet's current compliance status</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1 text-lg">•</span>
                  <span className="text-base text-gray-700">Requirements for common travel destinations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1 text-lg">•</span>
                  <span className="text-base text-gray-700">Any actions needed to maintain or achieve readiness</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1 text-lg">•</span>
                  <span className="text-base text-gray-700">Key timelines to be aware of</span>
                </li>
              </ul>
              <p className="text-lg text-gray-800 font-medium italic">
                This report serves as your clear, practical guide to pet travel preparedness.
              </p>
            </div>

            {/* Important Disclaimers */}
            <div className="bg-amber-50 rounded-2xl p-8 border-2 border-amber-200">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-amber-600">⚠️</span> Important to Know
              </h3>
              <p className="text-lg text-gray-700 mb-4 font-semibold">
                TravelSafe is a readiness and advisory service.
              </p>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1 text-lg">•</span>
                  <span className="text-base text-gray-700">It does not replace veterinary health certificates</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1 text-lg">•</span>
                  <span className="text-base text-gray-700">It does not guarantee entry into any country</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1 text-lg">•</span>
                  <span className="text-base text-gray-700">Travel requirements vary by jurisdiction and may change at any time</span>
                </li>
              </ul>
              <p className="text-lg text-gray-800 font-semibold">
                We are here to help you stay prepared — so you can travel with confidence.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Who It's For Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Who Is This For?
              </h2>
              <p className="text-xl text-gray-600">
                Perfect for Cayman Islands residents who want peace of mind
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 border border-teal-100">
              <div className="space-y-4">
                {whoItsFor.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                    <p className="text-lg text-gray-800">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Protect your whole fur family
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-teal-500">
              {/* Header */}
              <div className="bg-gradient-to-r from-teal-600 to-blue-900 text-white p-8 text-center">
                <h3 className="text-2xl font-bold mb-2">TravelSafe Annual Service</h3>
                <p className="text-teal-100">Complete peace of mind for your pet</p>
              </div>

              {/* Pricing */}
              <div className="p-8">
                {/* Mobile Service Note */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-700 text-center leading-relaxed">
                    <span className="font-semibold text-blue-900">Our TravelSafe specialists come directly to your home for convenient microchip verification and compliance checks.</span>
                    <br />
                    Island-wide mobile service included (remote area surcharge may apply).
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="text-center p-6 bg-teal-50 rounded-xl border-2 border-teal-200">
                    <div className="text-sm text-teal-700 font-semibold mb-2">First Pet</div>
                    <div className="text-5xl font-bold text-teal-700 mb-2">
                      CI$169
                      <span className="text-xl text-gray-600">/year</span>
                    </div>
                    <div className="text-sm text-gray-600">Complete mobile coverage</div>
                  </div>

                  <div className="text-center p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                    <div className="text-sm text-blue-700 font-semibold mb-2">Additional Pets</div>
                    <div className="text-5xl font-bold text-blue-700 mb-2">
                      CI$129
                      <span className="text-xl text-gray-600">/year</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="line-through text-gray-400">CI$169</span> Save CI$40 each
                    </div>
                  </div>
                </div>

                {/* Example Savings */}
                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 mb-8 border border-green-200">
                  <div className="text-center mb-4">
                    <span className="inline-block bg-green-100 text-green-800 px-4 py-1 rounded-full text-sm font-semibold">
                      Multi-Pet Savings
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">2 Pets</div>
                      <div className="text-sm text-gray-600">CI$298/year</div>
                      <div className="text-xs text-green-600 font-semibold">Save CI$40</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">3 Pets</div>
                      <div className="text-sm text-gray-600">CI$427/year</div>
                      <div className="text-xs text-green-600 font-semibold">Save CI$80</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">4 Pets</div>
                      <div className="text-sm text-gray-600">CI$556/year</div>
                      <div className="text-xs text-green-600 font-semibold">Save CI$120</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">5+ Pets</div>
                      <div className="text-sm text-gray-600">CI$685/year</div>
                      <div className="text-xs text-green-600 font-semibold">Save CI$160+</div>
                    </div>
                  </div>
                </div>

                {/* Remote Area Note */}
                <div className="text-center text-xs text-gray-500 mb-6">
                  *Remote areas (East End, North Side) may have a CI$20 travel surcharge
                </div>

                {/* CTA */}
                <div className="mt-8 text-center">
                  <button
                    onClick={() => navigate('/book-travelsafe')}
                    className="px-10 py-4 bg-gradient-to-r from-teal-600 to-blue-900 text-white rounded-lg font-bold text-lg hover:from-teal-700 hover:to-blue-950 transition-all duration-200 shadow-lg flex items-center gap-2 mx-auto"
                  >
                    Schedule Your TravelSafe Check
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <p className="text-sm text-gray-600 mt-4">
                    Less than CI$15 per month for complete peace of mind
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: 1,
                  title: "Schedule Your Annual Check",
                  description: "Quick booking for your pet(s) - contact us to arrange your appointment"
                },
                {
                  step: 2,
                  title: "Complete Your Annual Readiness Check",
                  description: "Microchip scan, vaccination review, and personalized travel consultation"
                },
                {
                  step: 3,
                  title: "Receive Your TravelSafe Readiness Report",
                  description: "Detailed assessment report showing completed requirements and outstanding items"
                },
                {
                  step: 4,
                  title: "Stay Travel-Ready All Year",
                  description: "Automatic reminders, year-round alerts, and ongoing support"
                }
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-900 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Banner */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-900 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Hurricane Season is Coming
          </h2>
          <p className="text-xl mb-8 text-teal-50">
            Don't wait until it's too late. Get your pet travel-ready today.
          </p>
          <button
            onClick={() => navigate('/book-travelsafe')}
            className="px-10 py-4 bg-white text-teal-700 rounded-lg font-bold text-lg hover:bg-teal-50 transition-all duration-200 shadow-xl inline-flex items-center gap-2"
          >
            Schedule Your Check
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
